import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import CloseIcon from '@mui/icons-material/Close';
import { DataGrid } from "@mui/x-data-grid";
import { useSnackbar } from "../../App";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";
import {
  deleteUser,
  getAllUsers,
  createUser,
  updateUser,
} from "../../service/userService";
import AddUser from "./AddUser";
import { getAllBranchLists } from "../../service/branchService";

const Users = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchStatus, setSearchStatus] = useState("All");
  const [searchBranch, setSearchBranch] = useState("All");
  const [branches, setBranches] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    branch_id: "",
    role_id: "",
    status: true,
  });
  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);
  const { showSnackbar } = useSnackbar();

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const sortField = sortModel[0]?.field || "created_at";
      const sortOrder = sortModel[0]?.sort || "desc";
      const res = await getAllUsers(
        page,
        limit,
        debouncedSearch,
        searchStatus,
        searchBranch,
        sortField,
        sortOrder
      );
      const data = res?.data?.data;

      if (Array.isArray(data?.data)) {
        const normalized = data.data.map((row) => ({
          id: row.id,
          name: row.name,
          email: row.email,
          role: row.role?.name || "N/A",
          role_id: row.role_id,
          branch: row.branch?.name || "N/A",
          branch_id: row.branch_id,
          status: row.status ? "Active" : "Inactive",
          created_at: row.created_at?.split("T")[0] || "",
        }));
        setRows(normalized);
        setRowCount(data.total);
      } else {
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      console.error("Fetch users error:", error);
      showSnackbar(error?.response?.data?.message || "Failed to fetch users!", "error");
    } finally {
      setLoading(false);
    }
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    debouncedSearch,
    searchStatus,
    searchBranch,
    sortModel,
    showSnackbar,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        setBranches(res?.data?.data || []);
      } catch (error) {
        console.error("Fetch branches error:", error);
        showSnackbar(error?.response?.data?.message || "Failed to fetch branches!", "error");
      }
    };
    fetchBranches();
  }, [showSnackbar]);

  const handleMenuOpen = (e, row) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  //delete 

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleDelete = () => {
  if (!selectedRow || !selectedRow.id) {
    showSnackbar("No user selected for deletion!", "error");
    return;
  }
  setOpenDeleteDialog(true);
 
};

const confirmDelete = async () => {
  if (!selectedRow || !selectedRow.id) {
    showSnackbar("No user selected for deletion!", "error");
    setOpenDeleteDialog(false);
    return;
  }
  try {
    setLoading(true);
    await deleteUser(selectedRow.id);
    showSnackbar("User deleted successfully!", "success");
    await fetchUsers();
    setPaginationModel((prev) => ({ ...prev, page: 0 })); 
  } catch (error) {
    console.error("Delete user error:", error);
    showSnackbar(error?.response?.data?.message || "Failed to delete user!", "error");
  } finally {
    setLoading(false);
    setOpenDeleteDialog(false);
    setSelectedRow(null); 
    setAnchorEl(null); 
  }
};

  const handleEdit = (row) => {
    setFormData({
      id: row.id,
      name: row.name,
      email: row.email,
      password: "",
      password_confirmation: "",
      role_id: row.role_id || "",
      branch_id: row.branch_id || "",
      status: row.status === "Active",
    });
    setOpenModal(true);
    handleMenuClose();
  };

  const handleCreate = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      branch_id: "",
      role_id: "",
      status: true,
    });
    setOpenModal(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (formData.id) {
        await updateUser(formData.id, formData);
        showSnackbar("User updated successfully!", "success");
      } else {
        await createUser(formData);
        showSnackbar("User created successfully!", "success");
      }
      setOpenModal(false);
      await fetchUsers();
    } catch (error) {
      throw error
      console.error("Submit user error:", error);
      showSnackbar(error?.response?.data?.message || "Something went wrong!", "error");
    } finally {
       
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.5,
      sortable: false,
      minWidth: isMobile ? 80 : 120,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderRadius: 1.5,
            padding: isMobile ? '10px' : '8px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.08)',
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: isMobile ? 120 : 180 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    { field: "role", headerName: "Role", flex: 1, minWidth: 180 },
    { field: "branch", headerName: "Branch", flex: 1, minWidth: 180 },
    { field: "created_at", headerName: "Created Date", flex: 1, minWidth: 180 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: isMobile ? 100 : 180,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          color={params.row.status === "Active" ? "success" : "error"}
          size="small"
          sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
        />
      ),
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          gap={isMobile ? 1 : 2}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search userName"
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearch("");
                      setDebouncedSearch("");
                      setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    }}
                  >
                    <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": { display: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
              width: isMobile ? "100%" : 180,
            }}
          />
          <FormControl
            sx={{
              width: isMobile ? "100%" : 150,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
            }}
          >
            <Select
              value={searchBranch}
              onChange={(e) => setSearchBranch(e.target.value)}
            >
              <MenuItem value="All">Branch</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            sx={{
              width: isMobile ? "100%" : 120,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
            }}
          >
            <Select
              value={searchStatus}
              onChange={(e) => setSearchStatus(e.target.value)}
            >
              <MenuItem value="All">Role</MenuItem>
              <MenuItem value="1">Admin</MenuItem>
              <MenuItem value="2">Manager</MenuItem>
              <MenuItem value="3">Sales</MenuItem>
            </Select>
          </FormControl>
          <Button
            onClick={() => {
              setSearch("");
              setDebouncedSearch("");
              setSearchStatus("All");
              setSearchBranch("All");
              setPaginationModel({ page: 0, pageSize: 10 });
            }}
            sx={{
              background: "none",
              color: Color.secondary.main,
              border: "1px solid",
              borderColor: Color.secondary.main,
              height: isMobile ? "34px" : "37.69px",
              width: isMobile ? "100%" : 90,
              fontSize: isMobile ? "0.85rem" : "1rem",
            }}
          >
            Clear
          </Button>
        </Box>
        <Button
          onClick={handleCreate}
          startIcon={<AddIcon />}
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : 120,
            fontSize: isMobile ? "0.85rem" : "1rem",
            mt: isMobile ? 0 : 0,
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
        >
          Add User
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id}
          rowHeight={isMobile ? 48 : 56}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: 500,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => handleEdit(selectedRow)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={handleDelete}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <AddUser
        open={openModal}
        onClose={() => setOpenModal(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        loading={loading}
      />

      <Dialog
  open={openDeleteDialog}
  onClose={() => {
    setOpenDeleteDialog(false);
    setSelectedRow(null); // Reset selectedRow on cancel
    setAnchorEl(null); // Close menu
  }}
  fullWidth
  maxWidth="xs"
  PaperProps={{
    sx: {
      width: isMobile ? "90vw" : "auto",
      m: isMobile ? 1 : 2,
    },
  }}
>
  <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
    Confirm Delete
  </DialogTitle>
  <DialogContent>
    <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
      Are you sure you want to delete <strong>{selectedRow?.name}</strong>?
    </Typography>
  </DialogContent>
  <DialogActions
    sx={{
      mr: 2,
      mb: 2,
      flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 1 : 0,
    }}
  >
    <Button
      onClick={() => {
        setOpenDeleteDialog(false);
        setSelectedRow(null); // Reset selectedRow on cancel
        setAnchorEl(null); // Close menu
      }}
      sx={{
        border: "1px solid",
        borderColor: Color.secondary.main,
        color: Color.label.main,
        width: isMobile ? "100%" : "auto",
        fontSize: isMobile ? "0.85rem" : "1rem",
        padding: isMobile ? "8px" : "6px 16px",
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={confirmDelete}
      sx={{
        background: Color.primary.main,
        color: "white",
        width: isMobile ? "100%" : "auto",
        fontSize: isMobile ? "0.85rem" : "1rem",
        padding: isMobile ? "8px" : "6px 16px",
        "&:hover": {
          background: Color.primary.dark,
        },
      }}
    >
      Delete
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default Users;